/**
 * Business establishment statistics (Census CBP-style sector × size classes).
 * Reads from Supabase `public.business_establishments`.
 *
 * Policy / totals: **sector** = "Total for all sectors" and **size** = "All establishments"
 * → **count** is the all-establishments total (used for building electrification commercial intensity).
 *
 * Charts:
 * - By **size**: rows with sector = total, size ≠ "All establishments"
 * - By **sector**: rows with size = "All establishments", sector ≠ total
 */

import { supabaseClient } from './supabase';

export const BUSINESS_ESTABLISHMENTS_TABLE = 'business_establishments';

/** Published total row labels (case-insensitive match after trim). */
export const SECTOR_TOTAL_FOR_POLICY = 'Total for all sectors';
export const SIZE_ALL_ESTABLISHMENTS = 'All establishments';

export interface BusinessEstablishmentRow {
  year: number;
  sector: string | null;
  size: string | null;
  count: number | null;
  num_employees: string | null;
}

export interface SectorSlice {
  sectorLabel: string;
  establishments: number;
}

export interface SizeSlice {
  sizeLabel: string;
  establishments: number;
}

/** Stacked area: establishments by sector, one value per year (aligned with `years`). */
export interface SectorStackedAreaSeries {
  years: number[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
  }[];
}

export interface BusinessEstablishmentsSummary {
  hasEstablishmentData: boolean;
  latestYear: number | null;
  /** From sector=Total for all sectors, size=All establishments (latest year). */
  totalEstablishments: number | null;
  totalEmployees: number | null;
  sectorCount: number | null;
  yearSpan: { first: number; last: number } | null;
  pctChangeEstablishments: number | null;
  topSectors: SectorSlice[];
  /** Under "Total for all sectors", excluding "All establishments". */
  sizeBreakdown: SizeSlice[];
  /** Employees ÷ establishments (policy total row) when both are numeric. */
  workersPerEstablishment: number | null;
  /** Top sectors over time + "All other sectors" for stacked area chart. */
  sectorStackedArea: SectorStackedAreaSeries | null;
  workplaceJobsLatest: number | null;
  workplaceJobsYear: number | null;
  establishmentsPer1kJobs: number | null;
  establishmentsPer1kPop: number | null;
  populationLatest: number | null;
  fetchWarning: string | null;
}

function normLabel(s: string | null | undefined): string {
  return (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function isTotalSector(sector: string | null | undefined): boolean {
  return normLabel(sector) === normLabel(SECTOR_TOTAL_FOR_POLICY);
}

function isAllEstablishmentsSize(size: string | null | undefined): boolean {
  return normLabel(size) === normLabel(SIZE_ALL_ESTABLISHMENTS);
}

function parseCount(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
  const s = String(v).replace(/,/g, '').trim();
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** Parse employee total from num_employees; ignore range text like "500 to 999 employees". */
function parseEmployeeTotal(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
  const s = String(v).replace(/,/g, '').trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function geofipsQueryVariants(geofips: string): string[] {
  const s = String(geofips).trim();
  const noLead = s.replace(/^0+/, '') || '0';
  const out = [noLead, s];
  if (/^\d+$/.test(s)) {
    out.push(s.padStart(7, '0'));
    out.push(noLead.padStart(7, '0'));
  }
  return Array.from(new Set(out));
}

/** Smallest numeric token in size label for sort order (e.g. "1 to 4" → 1). */
function sizeBracketSortKey(sizeLabel: string): number {
  const matches = sizeLabel.match(/[\d,]+/g);
  if (!matches?.length) return 1e12;
  const nums = matches
    .map((x) => parseInt(x.replace(/,/g, ''), 10))
    .filter((n) => Number.isFinite(n));
  return nums.length ? Math.min(...nums) : 1e12;
}

function totalRowForYear(rows: BusinessEstablishmentRow[]): { est: number; emp: number | null } {
  const hit = rows.find((r) => isTotalSector(r.sector) && isAllEstablishmentsSize(r.size));
  if (!hit) return { est: 0, emp: null };
  const est = parseCount(hit.count);
  const emp = parseEmployeeTotal(hit.num_employees);
  return { est, emp };
}

const SECTOR_STACK_TOP_N = 10;
const SECTOR_AREA_COLORS = [
  { bg: 'rgba(37, 99, 235, 0.55)', border: 'rgb(29, 78, 216)' },
  { bg: 'rgba(22, 163, 74, 0.55)', border: 'rgb(21, 128, 61)' },
  { bg: 'rgba(234, 88, 12, 0.55)', border: 'rgb(194, 65, 12)' },
  { bg: 'rgba(147, 51, 234, 0.5)', border: 'rgb(126, 34, 206)' },
  { bg: 'rgba(8, 145, 178, 0.55)', border: 'rgb(14, 116, 144)' },
  { bg: 'rgba(220, 38, 38, 0.5)', border: 'rgb(185, 28, 28)' },
  { bg: 'rgba(202, 138, 4, 0.55)', border: 'rgb(161, 98, 7)' },
  { bg: 'rgba(79, 70, 229, 0.5)', border: 'rgb(67, 56, 202)' },
  { bg: 'rgba(236, 72, 153, 0.45)', border: 'rgb(219, 39, 119)' },
  { bg: 'rgba(13, 148, 136, 0.5)', border: 'rgb(15, 118, 110)' },
];

/**
 * Stacked area data: top N sectors by latest-year count; remaining sectors roll into "All other sectors".
 */
function buildSectorStackedAreaSeries(
  byYear: Map<number, BusinessEstablishmentRow[]>,
  years: number[]
): SectorStackedAreaSeries | null {
  if (years.length === 0) return null;
  const lastY = years[years.length - 1];
  const latestRows = byYear.get(lastY) || [];
  const ranked = latestRows
    .filter((r) => isAllEstablishmentsSize(r.size) && !isTotalSector(r.sector) && parseCount(r.count) > 0)
    .sort((a, b) => parseCount(b.count) - parseCount(a.count));
  const seenNorm = new Set<string>();
  const topLabels: string[] = [];
  for (const r of ranked) {
    const lab = (r.sector || 'Unknown').trim();
    const k = normLabel(lab);
    if (seenNorm.has(k)) continue;
    seenNorm.add(k);
    topLabels.push(lab);
    if (topLabels.length >= SECTOR_STACK_TOP_N) break;
  }
  if (topLabels.length === 0) return null;

  const topNormSet = new Set(topLabels.map((l) => normLabel(l)));

  const datasets = topLabels.map((label, i) => {
    const c = SECTOR_AREA_COLORS[i % SECTOR_AREA_COLORS.length];
    return {
      label,
      data: years.map((y) => {
        const rows = byYear.get(y) || [];
        const hit = rows.find(
          (r) => isAllEstablishmentsSize(r.size) && normLabel(r.sector) === normLabel(label)
        );
        return hit ? parseCount(hit.count) : 0;
      }),
      backgroundColor: c.bg,
      borderColor: c.border,
    };
  });

  const otherData = years.map((y) => {
    const rows = byYear.get(y) || [];
    let sum = 0;
    for (const r of rows) {
      if (!isAllEstablishmentsSize(r.size) || isTotalSector(r.sector)) continue;
      const lab = (r.sector || '').trim();
      if (topNormSet.has(normLabel(lab))) continue;
      sum += parseCount(r.count);
    }
    return sum;
  });

  if (otherData.some((v) => v > 0)) {
    datasets.push({
      label: 'All other sectors',
      data: otherData,
      backgroundColor: 'rgba(148, 163, 184, 0.5)',
      borderColor: 'rgb(100, 116, 139)',
    });
  }

  return { years: [...years], datasets };
}

export async function fetchBusinessEstablishmentRows(geofips: string): Promise<{
  rows: BusinessEstablishmentRow[];
  error: string | null;
}> {
  const variants = geofipsQueryVariants(geofips);
  let lastError: string | null = null;

  for (const g of variants) {
    try {
      const { data, error } = await supabaseClient
        .from(BUSINESS_ESTABLISHMENTS_TABLE)
        .select('year, sector, size, count, num_employees')
        .eq('geofips', g)
        .order('year', { ascending: true });

      if (error) {
        const msg = error.message || String(error);
        lastError = msg;
        if (/does not exist|schema cache|permission denied/i.test(msg)) {
          return { rows: [], error: null };
        }
        continue;
      }
      const rows = (data as BusinessEstablishmentRow[]) || [];
      if (rows.length > 0) {
        return { rows, error: null };
      }
    } catch (e: unknown) {
      lastError = e instanceof Error ? e.message : String(e);
    }
  }

  return { rows: [], error: lastError };
}

/**
 * Latest-year establishment count from the policy row (Total for all sectors × All establishments).
 * For async wiring into building electrification / cost model.
 */
export async function fetchLatestTotalEstablishmentsForGeofips(
  geofips: string | number
): Promise<number | null> {
  const { rows, error } = await fetchBusinessEstablishmentRows(String(geofips));
  const summary = summarizeBusinessEstablishments(rows, null, null, error);
  if (!summary.hasEstablishmentData || summary.totalEstablishments == null) return null;
  return summary.totalEstablishments;
}

async function fetchWorkplaceJobsLatest(
  rawGeofips: string,
  geographyType: string
): Promise<{ jobs: number; year: number } | null> {
  const allYears = Array.from({ length: 25 }, (_, i) => 2005 + i);
  const workerGeofips =
    geographyType === 'State' ? '6' : geographyType === 'County' ? rawGeofips.slice(-4) : rawGeofips.slice(-6);
  const geoTypeLower =
    geographyType === 'State' ? 'state' : geographyType === 'County' ? 'county' : 'place';

  const { data, error } = await supabaseClient
    .from('lodes_employees_workplace')
    .select('year, total_worker')
    .eq('geofips', workerGeofips)
    .eq('geography_type', geoTypeLower)
    .in('year', allYears)
    .order('year', { ascending: true });

  if (error || !data?.length) return null;
  const last = data[data.length - 1];
  const jobs = parseCount(last.total_worker);
  if (jobs <= 0) return null;
  return { jobs, year: last.year };
}

async function fetchPopulationLatest(
  rawGeofips: string,
  geographyType: string
): Promise<{ pop: number; year: number } | null> {
  const { data, error } = await supabaseClient
    .from('population')
    .select('year, population')
    .eq('geofips', rawGeofips)
    .eq('geography_type', geographyType)
    .order('year', { ascending: true });

  if (error || !data?.length) return null;
  const last = data[data.length - 1];
  const pop = parseCount(last.population);
  if (pop <= 0) return null;
  return { pop, year: last.year };
}

export function summarizeBusinessEstablishments(
  rows: BusinessEstablishmentRow[],
  workplace: { jobs: number; year: number } | null,
  population: { pop: number; year: number } | null,
  fetchWarning: string | null
): BusinessEstablishmentsSummary {
  const byYear = new Map<number, BusinessEstablishmentRow[]>();
  for (const r of rows) {
    const y = Number(r.year);
    if (!Number.isFinite(y)) continue;
    const list = byYear.get(y) || [];
    list.push(r);
    byYear.set(y, list);
  }

  const years = Array.from(byYear.keys()).sort((a, b) => a - b);
  if (years.length === 0) {
    return {
      hasEstablishmentData: false,
      latestYear: null,
      totalEstablishments: null,
      totalEmployees: null,
      sectorCount: null,
      yearSpan: null,
      pctChangeEstablishments: null,
      topSectors: [],
      sizeBreakdown: [],
      workersPerEstablishment: null,
      sectorStackedArea: null,
      workplaceJobsLatest: workplace?.jobs ?? null,
      workplaceJobsYear: workplace?.year ?? null,
      establishmentsPer1kJobs: null,
      establishmentsPer1kPop: null,
      populationLatest: population?.pop ?? null,
      fetchWarning,
    };
  }

  const firstY = years[0];
  const lastY = years[years.length - 1];
  const firstAgg = totalRowForYear(byYear.get(firstY) || []);
  const lastAgg = totalRowForYear(byYear.get(lastY) || []);

  let pctChange: number | null = null;
  if (firstAgg.est > 0 && lastY > firstY) {
    pctChange = ((lastAgg.est - firstAgg.est) / firstAgg.est) * 100;
  }

  const latestRows = byYear.get(lastY) || [];

  const sectorRows = latestRows.filter(
    (r) => isAllEstablishmentsSize(r.size) && !isTotalSector(r.sector) && parseCount(r.count) > 0
  );
  const sectorKeys = new Set(sectorRows.map((r) => normLabel(r.sector)));
  const sectorCount = sectorKeys.size > 0 ? sectorKeys.size : null;

  const topSectors: SectorSlice[] = [...sectorRows]
    .sort((a, b) => parseCount(b.count) - parseCount(a.count))
    .slice(0, 12)
    .map((r) => ({
      sectorLabel: (r.sector || 'Unknown').trim(),
      establishments: parseCount(r.count),
    }));

  const sizeRows = latestRows.filter(
    (r) => isTotalSector(r.sector) && !isAllEstablishmentsSize(r.size) && parseCount(r.count) > 0
  );
  const sizeBreakdown: SizeSlice[] = [...sizeRows]
    .sort((a, b) => {
      const ka = sizeBracketSortKey((a.size || '').trim());
      const kb = sizeBracketSortKey((b.size || '').trim());
      return ka - kb;
    })
    .map((r) => ({
      sizeLabel: (r.size || 'Unknown').trim(),
      establishments: parseCount(r.count),
    }));

  const estPer1kJobs =
    workplace && workplace.jobs > 0 && lastAgg.est > 0 ? (lastAgg.est / workplace.jobs) * 1000 : null;
  const estPer1kPop =
    population && population.pop > 0 && lastAgg.est > 0 ? (lastAgg.est / population.pop) * 1000 : null;

  const hasData = lastAgg.est > 0;
  const workersPerEstablishment =
    hasData && lastAgg.est > 0 && lastAgg.emp != null && lastAgg.emp > 0 ? lastAgg.emp / lastAgg.est : null;
  const sectorStackedArea = hasData ? buildSectorStackedAreaSeries(byYear, years) : null;

  return {
    hasEstablishmentData: hasData,
    latestYear: hasData ? lastY : null,
    totalEstablishments: hasData ? lastAgg.est : null,
    totalEmployees: hasData && lastAgg.emp != null && lastAgg.emp > 0 ? lastAgg.emp : null,
    sectorCount,
    yearSpan: hasData ? { first: firstY, last: lastY } : null,
    pctChangeEstablishments: hasData ? pctChange : null,
    topSectors: hasData ? topSectors : [],
    sizeBreakdown: hasData ? sizeBreakdown : [],
    workersPerEstablishment,
    sectorStackedArea,
    workplaceJobsLatest: workplace?.jobs ?? null,
    workplaceJobsYear: workplace?.year ?? null,
    establishmentsPer1kJobs: estPer1kJobs,
    establishmentsPer1kPop: estPer1kPop,
    populationLatest: population?.pop ?? null,
    fetchWarning,
  };
}

export async function loadBusinessEstablishmentsSummary(
  locationName: string
): Promise<BusinessEstablishmentsSummary> {
  const { data: locationData, error: locErr } = await supabaseClient
    .from('population')
    .select('geofips, geography_type')
    .eq('location_name', locationName)
    .limit(1)
    .maybeSingle();

  if (locErr || !locationData) {
    return summarizeBusinessEstablishments([], null, null, locErr?.message || 'Location not found');
  }

  const rawGeofipsFull = String(locationData.geofips);
  const rawGeofipsStripped = rawGeofipsFull.replace(/^0+/, '') || rawGeofipsFull;
  const geographyType = locationData.geography_type || 'City';

  const [estRes, workplace, population] = await Promise.all([
    fetchBusinessEstablishmentRows(rawGeofipsFull),
    fetchWorkplaceJobsLatest(rawGeofipsStripped, geographyType),
    fetchPopulationLatest(rawGeofipsFull, geographyType),
  ]);

  return summarizeBusinessEstablishments(estRes.rows, workplace, population, estRes.error);
}

export function buildBusinessEstablishmentsNarrative(
  location: string,
  s: BusinessEstablishmentsSummary
): string {
  if (s.hasEstablishmentData && s.latestYear != null && s.totalEstablishments != null) {
    const trend =
      s.pctChangeEstablishments != null && s.yearSpan && s.yearSpan.last > s.yearSpan.first
        ? ` From ${s.yearSpan.first} to ${s.yearSpan.last}, the published total ${s.pctChangeEstablishments >= 0 ? 'rose' : 'fell'} by about ${Math.abs(s.pctChangeEstablishments).toFixed(1)}%.`
        : '';
    const sectors =
      s.sectorCount != null
        ? ` The latest year breaks out ${s.sectorCount} industry sectors (each row is “all establishment sizes” within that sector).`
        : '';
    const sizes =
      s.sizeBreakdown.length > 0
        ? ` Under “${SECTOR_TOTAL_FOR_POLICY},” establishments are also split by employment-size class, showing how much of the stock is very small vs. large sites.`
        : '';
    const jobs =
      s.workplaceJobsLatest != null
        ? ` For context, workplace jobs in the LODES series (latest available year) are about ${s.workplaceJobsLatest.toLocaleString()}.`
        : '';
    const policyNote = ` The building electrification cost layer uses the row with sector “${SECTOR_TOTAL_FOR_POLICY}” and size “${SIZE_ALL_ESTABLISHMENTS}” as the commercial establishment count when allocating commercial gas and electricity.`;
    return (
      `${location} shows about ${s.totalEstablishments.toLocaleString()} business establishments in ${s.latestYear}` +
      (s.totalEmployees != null
        ? `, with roughly ${s.totalEmployees.toLocaleString()} employees in that published total (when the source reports a numeric employee sum).`
        : '.') +
      trend +
      sectors +
      sizes +
      jobs +
      policyNote
    );
  }

  if (s.workplaceJobsLatest != null) {
    return (
      `Establishment counts from \`${BUSINESS_ESTABLISHMENTS_TABLE}\` are not loaded for ${location} in this app yet. ` +
      `As a related indicator, workplace jobs (LEHD LODES, ${s.workplaceJobsYear ?? 'latest'}) are about ${s.workplaceJobsLatest.toLocaleString()} — useful for scaling commercial building energy and employer-focused climate programs.`
    );
  }

  return `Business establishment data is not available for ${location} yet. When ${BUSINESS_ESTABLISHMENTS_TABLE} is populated for this geography, key metrics, sector mix, and size-class charts will appear here.`;
}
